import React, { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Stepper } from "@shared/components/Stepper";
import { PaymentDetail } from "@features/cx/payments/types";
import { DetailsStep } from "./stepComponents/DetailsStep";

import { RefreshCw } from "lucide-react";
import { ConfirmationStep } from "./stepComponents/ConfirmationStep";
import { ReviewStep } from "./stepComponents/ReviewStep";

interface RefundPaymentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  payment: PaymentDetail | null;
}

export const RefundPaymentDialog: React.FC<RefundPaymentDialogProps> = ({
  isOpen,
  onClose,
  payment,
}) => {
  const [refundData, setRefundData] = useState<any>(null);
  const [refundId, setRefundId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setRefundData(null);
      setRefundId(null);
    }
  }, [isOpen]);

  const steps = useMemo(
    () => [
      {
        title: "Details",
        slug: "details",
        component: (
          <DetailsStep
            payment={payment}
            refundData={refundData}
            setRefundData={setRefundData}
            onCancel={onClose}
          />
        ),
        optional: false,
      },
      {
        title: "Review",
        slug: "review",
        component: (
          <ReviewStep
            payment={payment}
            refundData={refundData}
            onConfirm={(onNext: any, refundId?: string) => {
              // Set the generated or API returned refund ID
              const generatedId = `RFD-${Math.floor(100000 + Math.random() * 900000)}`;
              setRefundId(refundId || generatedId);
              if (onNext) onNext();
            }}
          />
        ),
        optional: false,
      },
      {
        title: "Confirmation",
        slug: "confirmation",
        component: (
          <ConfirmationStep
            payment={payment}
            refundData={refundData}
            refundId={refundId}
            onClose={onClose}
          />
        ),
        optional: false,
      },
    ],
    [payment, refundData, refundId, onClose],
  );

  if (!payment) return null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-[800px] flex flex-col p-0 max-h-[90vh]">
        <DialogHeader className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <RefreshCw className="w-5 h-5 text-gray-700" />
            Refund Payment
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          <Stepper
            steps={steps}
            onStepChange={() => {}}
            onStepSave={async () => {}}
            enableApiSave={false}
            saveOnStepChange={false}
            trackDirtyState={false}
            autoSave={false}
            persistProgress={false}
            allowSkip={false}
            storageKey="refund-payment-modal"
            enableRouting={false}
            onComplete={() => {}}
            showDraftResume={false}
            onValidation={undefined}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
